import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { buildTaskMap, taskDateKey } from "../components/TodoCalendarView/helpers";

dayjs.extend(isSameOrBefore);

// calView values
export const CAL_VIEWS = ["day", "week", "month", "schedule"];

const useTodoCalendar = (tasks = [], onRangeChange) => {
  const [calView,    setCalView]    = useState("month");
  const [activeDate, setActiveDate] = useState(() => dayjs());
  const [direction,  setDirection]  = useState(0);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  // Derived period boundaries
  const currentMonth  = useMemo(() => activeDate.startOf("month"), [activeDate]);
  const weekStart     = useMemo(() => activeDate.startOf("week"),  [activeDate]);
  const weekEnd       = useMemo(() => weekStart.add(6, "day"),     [weekStart]);

  const taskMap = useMemo(() => buildTaskMap(tasks), [tasks]);

  // Always derived from taskMap so removing a task updates the open modal instantly
  const selectedDayTasks = useMemo(() => {
    if (!selectedDate) return [];
    return taskMap[selectedDate.format("YYYY-MM-DD")] || [];
  }, [taskMap, selectedDate]);

  const noDateTasks = useMemo(
    () => tasks.filter((t) => !taskDateKey(t)),
    [tasks]
  );

  // Count tasks in the visible period
  const periodTaskCount = useMemo(() => {
    return tasks.filter((t) => {
      const key = taskDateKey(t);
      if (!key) return false;
      const d = dayjs(key);
      if (calView === "day")
        return key === activeDate.format("YYYY-MM-DD");
      if (calView === "week")
        return !d.isBefore(weekStart, "day") && !d.isAfter(weekEnd, "day");
      // month + schedule
      return d.month() === currentMonth.month() && d.year() === currentMonth.year();
    }).length;
  }, [tasks, calView, activeDate, currentMonth, weekStart, weekEnd]);

  // Header title changes per view
  const headerTitle = useMemo(() => {
    if (calView === "day")
      return activeDate.format("dddd, MMMM D, YYYY");
    if (calView === "week") {
      const s = weekStart, e = weekEnd;
      if (s.month() === e.month())
        return `${s.format("MMMM D")} – ${e.format("D, YYYY")}`;
      return `${s.format("MMM D")} – ${e.format("MMM D, YYYY")}`;
    }
    if (calView === "schedule")
      return activeDate.format("MMMM D, YYYY");
    return currentMonth.format("MMMM YYYY");
  }, [calView, activeDate, currentMonth, weekStart, weekEnd]);

  // Is the active period the current one (for showing "Today" button)
  const isCurrentPeriodNow = useMemo(() => {
    if (calView === "day")      return activeDate.isSame(dayjs(), "day");
    if (calView === "schedule") return activeDate.isSame(dayjs(), "day");
    if (calView === "week")  return dayjs().isBetween
      ? dayjs().isBetween(weekStart, weekEnd, "day", "[]")
      : !dayjs().isBefore(weekStart, "day") && !dayjs().isAfter(weekEnd, "day");
    return currentMonth.isSame(dayjs().startOf("month"), "month");
  }, [calView, activeDate, currentMonth, weekStart, weekEnd]);

  // Use a ref so onRangeChange identity changes (from filter updates) don't
  // re-trigger the effect — only calView/activeDate changes should re-fetch.
  const onRangeChangeRef = useRef(onRangeChange);
  useEffect(() => { onRangeChangeRef.current = onRangeChange; }, [onRangeChange]);

  useEffect(() => {
    if (!onRangeChangeRef.current) return;
    let startDate, endDate;
    if (calView === "day") {
      startDate = activeDate.format("YYYY-MM-DD");
      endDate   = activeDate.format("YYYY-MM-DD");
    } else if (calView === "week") {
      startDate = activeDate.startOf("week").format("YYYY-MM-DD");
      endDate   = activeDate.startOf("week").add(6, "day").format("YYYY-MM-DD");
    } else if (calView === "schedule") {
      startDate = activeDate.format("YYYY-MM-DD");
      endDate   = null;
    } else {
      startDate = activeDate.startOf("month").subtract(1, "week").format("YYYY-MM-DD");
      endDate   = activeDate.endOf("month").add(1, "week").format("YYYY-MM-DD");
    }
    onRangeChangeRef.current({ startDate, endDate, calView });
  }, [calView, activeDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const goNext = useCallback(() => {
    setDirection(1);
    setActiveDate((d) => {
      if (calView === "day")  return d.add(1, "day");
      if (calView === "week") return d.add(1, "week");
      return d.add(1, "month");
    });
  }, [calView]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setActiveDate((d) => {
      if (calView === "day")  return d.subtract(1, "day");
      if (calView === "week") return d.subtract(1, "week");
      return d.subtract(1, "month");
    });
  }, [calView]);

  const goToday     = useCallback(() => { setDirection(0); setActiveDate(dayjs()); }, []);
  const selectDay   = useCallback((date) => setActiveDate(date), []);
  const handleDayClick = useCallback((date) => { setSelectedDate(date); setModalOpen(true); }, []);
  const closeModal  = useCallback(() => setModalOpen(false), []);

  return {
    calView, setCalView,
    activeDate, selectDay,
    currentMonth, weekStart, weekEnd,
    direction,
    taskMap, noDateTasks, periodTaskCount,
    headerTitle, isCurrentPeriodNow,
    modalOpen, selectedDate, selectedDayTasks,
    goNext, goPrev, goToday,
    handleDayClick, closeModal,
  };
};

export default useTodoCalendar;
